import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, TrendingUp, TrendingDown, CheckCircle, AlertTriangle,
  XCircle, DollarSign, Home, Calendar, Plus, Save, BarChart3,
  Lightbulb, Target, Percent, Clock, Activity
} from 'lucide-react';
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from 'axios';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

export default function PerformanceTracker() {
  const { dealId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [deal, setDeal] = useState(null);
  const [report, setReport] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [entryForm, setEntryForm] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    actual_rent: '',
    actual_vacancy_days: '0',
    actual_repairs: '0',
    actual_capex: '0',
    actual_property_taxes: '',
    actual_insurance: '',
    actual_management: '',
    actual_other_expenses: '0',
    notes: ''
  });

  useEffect(() => {
    fetchDeal();
    fetchEntries();
  }, [dealId]);

  useEffect(() => {
    if (entries.length > 0) {
      fetchReport();
    }
  }, [selectedYear, entries]);

  const fetchDeal = async () => {
    try {
      const response = await axios.get(`${API}/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeal(response.data);
    } catch (error) {
      toast.error('Failed to load deal');
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/deals/${dealId}/performance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Failed to load entries:', error);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/deals/${dealId}/performance/report?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      setReport(null);
    }
  };

  const handleSaveEntry = async () => {
    setSaving(true);
    try {
      const payload = {
        year: parseInt(entryForm.year),
        month: parseInt(entryForm.month),
        actual_rent: entryForm.actual_rent ? parseFloat(entryForm.actual_rent) : null,
        actual_vacancy_days: parseInt(entryForm.actual_vacancy_days) || 0,
        actual_repairs: parseFloat(entryForm.actual_repairs) || 0,
        actual_capex: parseFloat(entryForm.actual_capex) || 0,
        actual_property_taxes: entryForm.actual_property_taxes ? parseFloat(entryForm.actual_property_taxes) : null,
        actual_insurance: entryForm.actual_insurance ? parseFloat(entryForm.actual_insurance) : null,
        actual_management: entryForm.actual_management ? parseFloat(entryForm.actual_management) : null,
        actual_other_expenses: parseFloat(entryForm.actual_other_expenses) || 0,
        notes: entryForm.notes
      };

      await axios.post(`${API}/deals/${dealId}/performance`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Performance data saved!');
      setShowAddEntry(false);
      fetchEntries();
      
      // Reset form
      setEntryForm(prev => ({
        ...prev,
        actual_rent: '',
        actual_vacancy_days: '0',
        actual_repairs: '0',
        actual_capex: '0',
        actual_other_expenses: '0',
        notes: ''
      }));
    } catch (error) {
      toast.error('Failed to save performance data');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good':
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'poor':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'good':
        return 'text-emerald-500';
      case 'warning':
        return 'text-amber-500';
      case 'poor':
        return 'text-red-500';
      default:
        return 'text-slate-400';
    }
  };

  const getOverallStatusColor = (status) => {
    switch (status) {
      case 'excellent':
        return 'bg-emerald-500';
      case 'good':
        return 'bg-cyan-500';
      case 'fair':
        return 'bg-amber-500';
      case 'needs_attention':
        return 'bg-red-500';
      default:
        return 'bg-slate-500';
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  if (loading || !deal) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(`/deals/${dealId}`)} className="text-slate-400">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Deal
              </Button>
              <div className="flex items-center gap-2">
                <Activity className="h-6 w-6 text-cyan-500" />
                <h1 className="text-lg font-semibold text-white font-['Manrope']">Performance Tracker</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-32 bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>Year {y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => setShowAddEntry(true)}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Data
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Property Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-1">{deal.property_name}</h2>
          <p className="text-slate-400">Track actual vs. projected performance</p>
        </div>

        {/* Performance Report */}
        {report ? (
          <div className="space-y-6">
            {/* Overall Score Card */}
            <div className={`rounded-xl p-6 ${
              report.overall_status === 'excellent' ? 'bg-gradient-to-r from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30' :
              report.overall_status === 'good' ? 'bg-gradient-to-r from-cyan-500/20 to-cyan-500/5 border border-cyan-500/30' :
              report.overall_status === 'fair' ? 'bg-gradient-to-r from-amber-500/20 to-amber-500/5 border border-amber-500/30' :
              'bg-gradient-to-r from-red-500/20 to-red-500/5 border border-red-500/30'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-slate-400 mb-1">Year {report.year} Performance</div>
                  <div className="flex items-center gap-4">
                    <div className={`text-5xl font-bold ${
                      report.overall_status === 'excellent' ? 'text-emerald-500' :
                      report.overall_status === 'good' ? 'text-cyan-500' :
                      report.overall_status === 'fair' ? 'text-amber-500' :
                      'text-red-500'
                    }`}>
                      {report.overall_accuracy}%
                    </div>
                    <div>
                      <div className="text-white font-semibold text-lg">
                        {report.overall_status === 'excellent' ? 'Excellent Accuracy' :
                         report.overall_status === 'good' ? 'Good Accuracy' :
                         report.overall_status === 'fair' ? 'Fair Accuracy' :
                         'Needs Attention'}
                      </div>
                      <div className="text-sm text-slate-400">
                        Actual vs. Projected
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`p-4 rounded-full ${getOverallStatusColor(report.overall_status)}/20`}>
                  <Target className={`h-12 w-12 ${
                    report.overall_status === 'excellent' ? 'text-emerald-500' :
                    report.overall_status === 'good' ? 'text-cyan-500' :
                    report.overall_status === 'fair' ? 'text-amber-500' :
                    'text-red-500'
                  }`} />
                </div>
              </div>
            </div>

            {/* Metric Comparisons */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Projected vs Actual</h3>
              <div className="space-y-4">
                {report.comparisons.map((comp, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(comp.status)}
                      <div>
                        <div className="text-white font-medium">{comp.label}</div>
                        <div className="text-xs text-slate-500">
                          {comp.is_better_higher ? 'Higher is better' : 'Lower is better'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-8">
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Projected</div>
                        <div className="text-slate-300 font-mono">
                          {comp.metric.includes('rate') ? `${comp.projected}%` : formatCurrency(comp.projected)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Actual</div>
                        <div className={`font-mono font-semibold ${getStatusColor(comp.status)}`}>
                          {comp.metric.includes('rate') ? `${comp.actual}%` : formatCurrency(comp.actual)}
                        </div>
                      </div>
                      <div className={`text-right min-w-[80px] ${getStatusColor(comp.status)}`}>
                        <div className="flex items-center justify-end gap-1">
                          {comp.difference > 0 ? (
                            <TrendingUp className="h-4 w-4" />
                          ) : comp.difference < 0 ? (
                            <TrendingDown className="h-4 w-4" />
                          ) : null}
                          <span className="font-semibold">
                            {comp.difference_percent > 0 ? '+' : ''}{comp.difference_percent}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Model observations and planning prompts */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Model observations */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-cyan-500" />
                  Model Observations
                </h3>
                <ul className="space-y-3">
                  {report.insights.map((insight, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-300">
                      <span className="text-cyan-500 mt-1">•</span>
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Planning prompts */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  Planning Prompts
                </h3>
                <ul className="space-y-3">
                  {report.tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-300">
                      <span className="text-amber-500 mt-1">💡</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-xs text-slate-500 text-center">
              Performance comparisons and planning prompts are informational
              estimates, not financial or investment advice.
            </p>
          </div>
        ) : (
          /* No Data State */
          <div className="glass-card rounded-xl p-12 text-center">
            <Activity className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Performance Data Yet</h3>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              Start tracking your actual performance by adding monthly data. 
              See how your investment compares to projections.
            </p>
            <Button
              onClick={() => setShowAddEntry(true)}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Entry
            </Button>
          </div>
        )}

        {/* Data Entries Table */}
        {entries.length > 0 && (
          <div className="mt-8 glass-card rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Monthly Entries</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Period</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Rent</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Vacancy Days</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Repairs</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.filter(e => e.year === selectedYear).map((entry, i) => (
                    <tr key={i} className="border-b border-slate-800/50">
                      <td className="py-3 px-4 text-white">
                        {entry.month ? `${months[entry.month - 1]} ${entry.year}` : `Year ${entry.year}`}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-300">
                        {entry.actual_rent ? formatCurrency(entry.actual_rent) : '-'}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-300">
                        {entry.actual_vacancy_days || 0}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-300">
                        {formatCurrency(entry.actual_repairs || 0)}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-400 truncate max-w-[150px]">
                        {entry.notes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Add Entry Modal */}
      <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Add Performance Data</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Period Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Year</Label>
                <Select 
                  value={entryForm.year.toString()} 
                  onValueChange={(v) => setEntryForm({...entryForm, year: parseInt(v)})}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Month</Label>
                <Select 
                  value={entryForm.month.toString()} 
                  onValueChange={(v) => setEntryForm({...entryForm, month: parseInt(v)})}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {months.map((m, i) => (
                      <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Income */}
            <div>
              <Label className="text-slate-300">Actual Rent Collected ($)</Label>
              <Input
                type="number"
                value={entryForm.actual_rent}
                onChange={(e) => setEntryForm({...entryForm, actual_rent: e.target.value})}
                placeholder="Monthly rent collected"
                className="bg-slate-950 border-slate-800 text-white"
              />
            </div>

            {/* Vacancy */}
            <div>
              <Label className="text-slate-300">Vacancy Days</Label>
              <Input
                type="number"
                value={entryForm.actual_vacancy_days}
                onChange={(e) => setEntryForm({...entryForm, actual_vacancy_days: e.target.value})}
                placeholder="0"
                className="bg-slate-950 border-slate-800 text-white"
              />
            </div>

            {/* Expenses */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Repairs ($)</Label>
                <Input
                  type="number"
                  value={entryForm.actual_repairs}
                  onChange={(e) => setEntryForm({...entryForm, actual_repairs: e.target.value})}
                  placeholder="0"
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">CapEx ($)</Label>
                <Input
                  type="number"
                  value={entryForm.actual_capex}
                  onChange={(e) => setEntryForm({...entryForm, actual_capex: e.target.value})}
                  placeholder="0"
                  className="bg-slate-950 border-slate-800 text-white"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-slate-300">Notes</Label>
              <Textarea
                value={entryForm.notes}
                onChange={(e) => setEntryForm({...entryForm, notes: e.target.value})}
                placeholder="Any notes about this month..."
                className="bg-slate-950 border-slate-800 text-white"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEntry(false)} className="border-slate-700 text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEntry} 
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              {saving ? 'Saving...' : 'Save Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
